import React, { Dispatch, SetStateAction, useEffect, useRef } from "react";
import classNames from "classnames";
import { NavLink } from "react-router-dom";
import { Navigation } from "../../types/navs";
import { Svg } from "../__common/Svg/Svg";
import { Typography } from "../__common/Typography/Typography";
import { Container } from "../Grid/Grid";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { GA4EventName } from "../../types/analytics";
import useOutsideClick from "../../hooks/outside-click-hooks";
import GoogleAnalyticsV2 from "../../utils/GoogleAnalyticsV2";

interface Props {
  navigation: Navigation;
  setShowSubMenu: Dispatch<SetStateAction<boolean>>;
  isActive?: boolean;
}

export const SubNav: React.FC<Props> = (props) => {
  const { t } = useTranslation();
  const { navigation, isActive = false, setShowSubMenu } = props;
  const [activeRightContent, setActiveRightContent] = useState<any>();

  const subNavRef = React.useRef(null);

  useOutsideClick({ ref: subNavRef, handler: () => setShowSubMenu(false) });

  const focusRef = useRef(null);

  const handleFocus = (value: any) => {
    if (focusRef.current) {
      setActiveRightContent(value);
    }
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Tab") {
        setTimeout(() => {
          const focusedElement = document.activeElement;
          if (subNavRef.current && focusedElement) {
            if (!(subNavRef.current as HTMLElement).contains(focusedElement)) {
              setShowSubMenu(false);
            }
          }
        }, 0);
      }
    };

    if (isActive) {
      document.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isActive, setShowSubMenu]);

  return (
    <div
      data-testid="subnav-item"
      onMouseLeave={() => setShowSubMenu(false)}
      className={classNames("sub-nav", {
        active: isActive,
      })}
    >
      <Container className="sub-nav__content">
        <div className="sub-nav__content__left" ref={subNavRef}>
          <div className="sub-links">
            {navigation.items && (
              <ul>
                {navigation.items.map((item) => {
                  return (
                    <li key={item.text}>
                      <Typography variant="body2">
                        <NavLink
                          ref={focusRef}
                          onFocus={() => handleFocus(item)}
                          data-testid="nav-items"
                          to={item.link || "/"}
                          onMouseEnter={() => {
                            setActiveRightContent(item);
                          }}
                          onMouseLeave={() => setActiveRightContent(undefined)}
                          onClick={() => {
                            GoogleAnalyticsV2.trackEvent(
                              GA4EventName.HeaderClick,
                              { action: item.gaAction }
                            );
                            setShowSubMenu(false);
                          }}
                          activeClassName="active"
                        >
                          {t(item.text)}
                        </NavLink>
                      </Typography>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
        {activeRightContent && (
          <div
            data-testid="subnav-content"
            className="sub-nav__content__right"
            key={activeRightContent.id}
          >
            {activeRightContent.detail && (
              <>
                <div className="sub-nav__content__right__text">
                  <Typography variant="body1">
                    {t(activeRightContent.detail?.text)}
                  </Typography>
                </div>
                <div className="sub-nav__content__right__icon">
                  <Svg
                    icon={activeRightContent.detail?.svg}
                    width={236}
                    height={181}
                    title={
                      activeRightContent.id === 1
                        ? t("PRIVACY_HEADER_SUBMENU_OVERVIEW_ALT_TEXT")
                        : t("PRIVACY_HEADER_SUBMENU_GPP_ALT_TEXT")
                    }
                  />
                </div>
              </>
            )}
          </div>
        )}
      </Container>
    </div>
  );
};
